// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { subnavFor, type Section } from './navModel';
import { useBucketCounts } from '../../hooks/useAlarms';
import { usePersona } from '../../hooks/usePersona';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useApprovals } from '../../hooks/useApprovals';
import { HITL_SITE_ID, HITL_APPROVAL_TYPE } from '../../utils/constants';

/** Map a pathname to its top-level section for subnav selection. */
function sectionForPath(pathname: string): Section | null {
  if (pathname.startsWith('/home')) return 'home';
  if (pathname.startsWith('/site')) return 'site';
  if (pathname.startsWith('/fleet')) return 'fleet';
  return null; // log/audit/prefs/etc. have no subnav
}

/**
 * 200px contextual subnav. Rendered only for sections that define items
 * (Home, Site, Fleet). Alarm buckets show live count pills.
 */
export default function ContextualSubnav() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { persona } = usePersona();
  const { siteId: activeSiteId } = useActiveSite();
  const siteId = params.site_id ?? activeSiteId;

  const section = sectionForPath(location.pathname);
  const countsScope = persona === 'operator' ? activeSiteId : undefined;
  const { counts } = useBucketCounts(countsScope);

  const { title, items } = section ? subnavFor(section, siteId) : { title: '', items: [] };
  const needsPendingApprovals = items.some(item => item.pendingApprovals);
  const { approvals } = useApprovals({
    siteId: HITL_SITE_ID,
    approvalType: HITL_APPROVAL_TYPE,
    enabled: needsPendingApprovals,
  });
  const pendingApprovalsCount = approvals.filter(a => a.state === 'PENDING').length;

  if (!section || items.length === 0) return null;

  return (
    <nav className="helios-subnav" aria-label={title}>
      <div className="helios-subnav-title">{title}</div>
      {items.map(item => {
        const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
        const count = item.pendingApprovals
          ? pendingApprovalsCount
          : item.bucket
            ? counts[item.bucket]
            : undefined;
        return (
          <button
            key={item.to}
            type="button"
            className={`helios-subnav-item${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => navigate(item.to)}
          >
            <span>{item.label}</span>
            {count != null && count > 0 && (
              <span
                className="helios-count-pill"
                style={item.accent ? { background: item.accent } : undefined}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
